import React, { useState, useEffect } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { createSystemInfo, updateSystemInfo, deleteSystemInfo } from '../graphql/mutations';
import { listSystemInfos } from '../graphql/queries';
import { makeStyles } from '@material-ui/core/styles';
import { Box, TextField, Button, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@material-ui/core';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(3),
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(6),
    },
  },
  textField: {
    margin: theme.spacing(1),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '50%',
    },
  },
  submitButton: {
    margin: theme.spacing(3, 0),
  },
  tableContainer: {
    boxShadow: '0px 8px 16px 0px rgba(0,0,0,0.1)',
    borderRadius: '12px',
    overflow: 'hidden',
    marginTop: '32px',
    backgroundColor: '#f9f9f9'
  },
  tableHeader: {
    backgroundColor: '#01b5e2',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '1rem'
  },
  tableRow: {
    '&:hover': {
      backgroundColor: '#e5f7fb',
    },
  },
  tableCell: {
    padding: theme.spacing(1.5, 2),
    fontSize: '0.9rem'
  },
  actionButton: {
    color: '#01b5e2',
    borderColor: '#01b5e2',
    '&:hover': {
      backgroundColor: '#01b5e2',
      color: 'white',
    }
  }
}));

const SystemSettings = () => {
  const classes = useStyles();
  const [systemInfos, setSystemInfos] = useState([]);
  const [newInfo, setNewInfo] = useState({ name: '', value: '', options: '' });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchSystemInfos();
  }, []);

  const fetchSystemInfos = async () => {
    try {
      const response = await API.graphql(graphqlOperation(listSystemInfos));
      setSystemInfos(response.data.listSystemInfos.items);
    } catch (error) {
      console.error('Error fetching system infos:', error);
    }
  };

  const handleInputChange = (name, value) => {
    setNewInfo({ ...newInfo, [name]: value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const input = { name: newInfo.name, value: newInfo.value, options: newInfo.options };
    if (editingId) {
      await API.graphql(graphqlOperation(updateSystemInfo, { input: { id: editingId, ...input } }));
    } else {
      await API.graphql(graphqlOperation(createSystemInfo, { input }));
    }
    setNewInfo({ name: '', value: '', options: '' });
    setEditingId(null);
    fetchSystemInfos();
  };

  const handleEdit = (id) => {
    const info = systemInfos.find(info => info.id === id);
    if (info) {
      setNewInfo({ name: info.name, value: info.value, options: info.options });
      setEditingId(id);
    }
  };

  return (
    <div>
      <Box className={classes.root} component="form" onSubmit={handleSubmit}>
        <Typography variant="h4" component="h1" gutterBottom>
          System Info Manager
        </Typography>
        <TextField
          className={classes.textField}
          required
          label="Name"
          value={newInfo.name}
          onChange={(e) => handleInputChange('name', e.target.value)}
        />
        <TextField
          className={classes.textField}
          label="Value"
          value={newInfo.value}
          onChange={(e) => handleInputChange('value', e.target.value)}
        />
        <TextField
          className={classes.textField}
          label="Options"
          value={newInfo.options}
          onChange={(e) => handleInputChange('options', e.target.value)}
        />
        <Button type="submit" variant="contained" color="primary" className={classes.submitButton}>
          {editingId ? 'Update Info' : 'Add Info'}
        </Button>
      </Box>
      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow className={classes.tableHeader}>
              <TableCell className={classes.tableCell}>Name</TableCell>
              <TableCell className={classes.tableCell}>Value</TableCell>
              <TableCell className={classes.tableCell}>Options</TableCell>
              <TableCell className={classes.tableCell}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {systemInfos.map((info) => (
              <TableRow key={info.id} className={classes.tableRow}>
                <TableCell className={classes.tableCell}>{info.name}</TableCell>
                <TableCell className={classes.tableCell}>{info.value}</TableCell>
                <TableCell className={classes.tableCell}>{info.options}</TableCell>
                <TableCell className={classes.tableCell}>
                  <Button variant="outlined" className={classes.actionButton} onClick={() => handleEdit(info.id)}>Edit</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default SystemSettings;
